import { Button } from "@/components/ui/button";
import { useState } from "react";
import { BookPreview, BookPreviewData } from "../preview/BookPreview";
import { getBookStatusColor, getBookStatusLabel } from "../../../utils/bookStatus";

interface BookHeaderProps {
  bookTitle: string;
  bookStatus: string;
  bookData?: BookPreviewData;
  onSave?: () => void;
  onImport?: (importType: 'json' | 'dlow') => void;
  t: (key: string) => string;
}

export function BookHeader({ bookTitle, bookStatus, bookData, onSave, onImport, t }: BookHeaderProps) {
  const [showPreview, setShowPreview] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);

  return (
    <>
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left side - Book info */}
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                  {bookTitle || "Sách chưa có tiêu đề"}
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBookStatusColor(bookStatus)}`}>
                    {getBookStatusLabel(bookStatus)}
                  </span>
                </div>
              </div>
            </div>

            {/* Right side - Actions */}
            <div className="flex items-center gap-2">
              {/* Import Dropdown */}
              {onImport && (
                <div className="relative">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowImportDropdown(!showImportDropdown)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                      <polyline points="7 10 12 15 17 10"></polyline>
                      <line x1="12" y1="15" x2="12" y2="3"></line>
                    </svg>
                    Import nội dung
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="ml-2">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </Button>
                  
                  {showImportDropdown && (
                    <>
                      {/* Backdrop to close dropdown */}
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setShowImportDropdown(false)}
                      />
                      
                      {/* Dropdown menu */}
                      <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20">
                        <div className="py-1" role="menu">
                          <button
                            onClick={() => {
                              setShowImportDropdown(false);
                              onImport('json');
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline mr-2">
                              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                              <polyline points="14 2 14 8 20 8"></polyline>
                              <line x1="16" y1="13" x2="8" y2="13"></line>
                              <line x1="16" y1="17" x2="8" y2="17"></line>
                              <polyline points="10 9 9 9 8 9"></polyline>
                            </svg>
                            Import từ JSON
                          </button>
                          <button
                            onClick={() => {
                              setShowImportDropdown(false);
                              onImport('dlow');
                            }}
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            role="menuitem"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="inline mr-2">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                              <polyline points="17 8 12 3 7 8"></polyline>
                              <line x1="12" y1="3" x2="12" y2="15"></line>
                            </svg>
                            Import từ Dlow JSON
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setShowPreview(true)}
                disabled={!bookData}
                title={!bookData ? "Chưa có dữ liệu sách để xem trước" : "Xem trước sách"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                  <circle cx="12" cy="12" r="3"></circle>
                </svg>
                Xem trước
              </Button>
              <Button variant="outline" size="sm" onClick={onSave}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                Lưu
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && bookData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-11/12 h-5/6 max-w-7xl">
            <BookPreview
              bookData={bookData}
              onClose={() => setShowPreview(false)}
              showNavigation={true}
              showHeader={true}
              className="h-full"
            />
          </div>
        </div>
      )}
    </>
  );
}
